import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History } from 'lucide-react';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';

interface SessionSummary {
  id: string;
  title: string | null;
  team_name: string;
  message_count: number;
  last_message_at: string | null;
  last_message_preview: { role: string; content: string; created_at: string } | null;
}

interface ChatHistoryDropdownProps {
  /** Scope the list to the currently-open team. */
  teamId: number;
  /** The session the drawer is currently showing; highlighted in the list. */
  currentSessionId: string | null;
  /** Called with the chosen session id when a row is clicked. */
  onSelect: (id: string) => void;
}

/**
 * Header control that opens a popover of the user's prior chat sessions
 * for the current team. Fetches `/api/ai/chat/sessions/?team_id=...` when
 * the popover is opened and refetches on every open so freshly-created
 * sessions appear without a page reload.
 */
export const ChatHistoryDropdown: React.FC<ChatHistoryDropdownProps> = ({
  teamId, currentSessionId, onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; right: number } | null>(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access');
      const resp = await fetch(
        getApiUrl(`/api/ai/chat/sessions/?team_id=${teamId}`),
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const result = await safeJsonParse<{ sessions: SessionSummary[] }>(
        resp, 'fetching chat sessions'
      );
      if (!result.success) throw new Error(result.error || 'Failed to load history');
      setSessions(result.data?.sessions ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (open) fetchSessions();
  }, [open, fetchSessions]);

  // Click-outside close. The popover is portaled to document.body to
  // escape the drawer's stacking context (so the ASK AI tab can't paint
  // over it), so contains() must check both the trigger root and the
  // detached popover element.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Anchor the portaled popover to the History button's right edge.
  // Recompute on open + on resize/scroll so it tracks the button if the
  // drawer or page layout shifts while open.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 4,
        right: Math.max(0, window.innerWidth - rect.right),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className="text-xs text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-200 px-2 py-1 flex items-center gap-1"
        aria-label="Show chat history"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <History className="w-3.5 h-3.5" />
        History
      </button>
      {open && popoverPos && createPortal(
        <div
          ref={popoverRef}
          role="listbox"
          style={{ position: 'fixed', top: popoverPos.top, right: popoverPos.right }}
          className="w-80 max-h-96 overflow-y-auto bg-white border border-neutral-200 dark:bg-neutral-900 dark:border-white/15 z-50 shadow-xl"
        >
          {loading && (
            <div className="px-3 py-2 text-sm text-neutral-500 italic">Loading…</div>
          )}
          {error && !loading && (
            <div className="px-3 py-2 text-sm text-red-500 dark:text-red-400">{error}</div>
          )}
          {!loading && !error && sessions && sessions.length === 0 && (
            <div className="px-3 py-2 text-sm text-neutral-500">
              No prior chats on this team yet.
            </div>
          )}
          {!loading && !error && sessions && sessions.map(s => {
            const isCurrent = s.id === currentSessionId;
            const label = s.title?.trim() || s.last_message_preview?.content?.slice(0, 60) || 'Untitled chat';
            const when = s.last_message_at ? formatRelative(s.last_message_at) : '';
            return (
              <button
                key={s.id}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => { onSelect(s.id); setOpen(false); }}
                className={`block w-full text-left px-3 py-2 border-b border-neutral-100 dark:border-white/5 last:border-b-0 ${
                  isCurrent
                    ? 'bg-neutral-100 dark:bg-white/10'
                    : 'hover:bg-neutral-50 dark:hover:bg-white/5'
                }`}
              >
                <div className="text-sm text-neutral-900 dark:text-neutral-100 truncate">{label}</div>
                <div className="text-[11px] text-neutral-500 flex justify-between">
                  <span>{s.message_count} msg{s.message_count === 1 ? '' : 's'}</span>
                  {when && <span>{when}</span>}
                </div>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

/** Compact human-readable relative time. "2h ago", "3d ago", "Jan 5". */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
